import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Store, 
  Eye, 
  EyeOff, 
  Calendar, 
  TrendingUp, 
  Award,
  Clock,
  MapPin,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const VendorAnalytics = () => {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBoxes();
  }, []);

  const fetchBoxes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendor/boxes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBoxes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching boxes:', error);
      toast.error('Failed to load analytics data');
      setBoxes([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics data
  const analytics = {
    totalStalls: boxes.length,
    activeStalls: boxes.filter(b => b.is_active).length,
    inactiveStalls: boxes.filter(b => !b.is_active).length,
    claimedStalls: boxes.filter(b => b.is_claimed).length,
    pendingApproval: boxes.filter(b => b.moderation_status === 'pending').length,
    categories: [...new Set(boxes.map(b => b.category))].length,
    oldestStall: boxes.length > 0 
      ? boxes.reduce((oldest, box) => 
          new Date(box.created_at) < new Date(oldest.created_at) ? box : oldest
        )
      : null,
    newestStall: boxes.length > 0 
      ? boxes.reduce((newest, box) => 
          new Date(box.created_at) > new Date(newest.created_at) ? box : newest
        )
      : null,
  };

  // Calculate days since first stall
  const daysSinceFirstStall = analytics.oldestStall 
    ? Math.floor((new Date() - new Date(analytics.oldestStall.created_at)) / (1000 * 60 * 60 * 24))
    : 0;

  // Calculate category breakdown
  const categoryBreakdown = boxes.reduce((acc, box) => {
    acc[box.category] = (acc[box.category] || 0) + 1;
    return acc;
  }, {});

  const categoryLabels = {
    fruits: 'Fruits',
    vegetables: 'Vegetables',
    flowers: 'Flowers & Plants',
    eggs: 'Eggs',
    jams: 'Honey & Preserves',
    baked_goods: 'Baked Goods',
    homegrown: 'Homegrown Produce',
    homemade: 'Homemade Crafts',
    upcycled: 'Upcycled Items',
    secondhand: 'Secondhand Goods',
    other: 'Other',
  };

  const categoryColors = {
    fruits: 'bg-red-100 text-red-700 border-red-200',
    vegetables: 'bg-green-100 text-green-700 border-green-200',
    flowers: 'bg-pink-100 text-pink-700 border-pink-200',
    eggs: 'bg-orange-100 text-orange-700 border-orange-200',
    jams: 'bg-amber-100 text-amber-700 border-amber-200',
    baked_goods: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    homegrown: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    homemade: 'bg-purple-100 text-purple-700 border-purple-200',
    upcycled: 'bg-blue-100 text-blue-700 border-blue-200',
    secondhand: 'bg-gray-100 text-gray-700 border-gray-200',
    other: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-analytics">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800" data-testid="analytics-title">Analytics Dashboard</h2>
        <p className="text-gray-600 mt-1">Overview of your honesty box performance</p>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Stalls</p>
                <p className="text-3xl font-bold text-blue-800" data-testid="stat-total">{analytics.totalStalls}</p>
              </div>
              <Store className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Active</p>
                <p className="text-3xl font-bold text-green-800" data-testid="stat-active">{analytics.activeStalls}</p>
              </div>
              <Eye className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Inactive</p>
                <p className="text-3xl font-bold text-gray-800" data-testid="stat-inactive">{analytics.inactiveStalls}</p>
              </div>
              <EyeOff className="w-10 h-10 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Days Active</p>
                <p className="text-3xl font-bold text-purple-800" data-testid="stat-days">{daysSinceFirstStall}</p>
              </div>
              <Calendar className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown & Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Category Breakdown
            </CardTitle>
            <CardDescription>Distribution of your stalls by category</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(categoryBreakdown).length === 0 ? (
              <p className="text-gray-500 text-center py-4">No stalls yet</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(categoryBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={categoryColors[category] || 'bg-gray-100'}>
                          {categoryLabels[category] || category}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-2 bg-green-500 rounded-full" 
                          style={{ width: `${(count / analytics.totalStalls) * 100}px`, minWidth: '20px' }}
                        />
                        <span className="text-sm font-medium text-gray-700 w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Milestones & Achievements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Milestones
            </CardTitle>
            <CardDescription>Your vendor journey achievements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* First Stall Achievement */}
              <div className={`flex items-center gap-3 p-3 rounded-lg ${analytics.totalStalls >= 1 ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className={`p-2 rounded-full ${analytics.totalStalls >= 1 ? 'bg-green-100' : 'bg-gray-200'}`}>
                  <CheckCircle className={`w-5 h-5 ${analytics.totalStalls >= 1 ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className={`font-medium ${analytics.totalStalls >= 1 ? 'text-green-800' : 'text-gray-500'}`}>
                    First Stall Created
                  </p>
                  <p className="text-sm text-gray-500">
                    {analytics.oldestStall ? formatDate(analytics.oldestStall.created_at) : 'Not yet achieved'}
                  </p>
                </div>
              </div>

              {/* 3 Stalls Achievement */}
              <div className={`flex items-center gap-3 p-3 rounded-lg ${analytics.totalStalls >= 3 ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className={`p-2 rounded-full ${analytics.totalStalls >= 3 ? 'bg-green-100' : 'bg-gray-200'}`}>
                  <CheckCircle className={`w-5 h-5 ${analytics.totalStalls >= 3 ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className={`font-medium ${analytics.totalStalls >= 3 ? 'text-green-800' : 'text-gray-500'}`}>
                    Multi-Stall Vendor
                  </p>
                  <p className="text-sm text-gray-500">
                    {analytics.totalStalls >= 3 ? '3+ stalls created!' : `${3 - analytics.totalStalls} more stall(s) needed`}
                  </p>
                </div>
              </div>

              {/* 30 Days Active Achievement */}
              <div className={`flex items-center gap-3 p-3 rounded-lg ${daysSinceFirstStall >= 30 ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className={`p-2 rounded-full ${daysSinceFirstStall >= 30 ? 'bg-green-100' : 'bg-gray-200'}`}>
                  <CheckCircle className={`w-5 h-5 ${daysSinceFirstStall >= 30 ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className={`font-medium ${daysSinceFirstStall >= 30 ? 'text-green-800' : 'text-gray-500'}`}>
                    Month-Long Vendor
                  </p>
                  <p className="text-sm text-gray-500">
                    {daysSinceFirstStall >= 30 ? 'Active for 30+ days!' : `${30 - daysSinceFirstStall} days to go`}
                  </p>
                </div>
              </div>

              {/* Multiple Categories Achievement */}
              <div className={`flex items-center gap-3 p-3 rounded-lg ${analytics.categories >= 2 ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className={`p-2 rounded-full ${analytics.categories >= 2 ? 'bg-green-100' : 'bg-gray-200'}`}>
                  <CheckCircle className={`w-5 h-5 ${analytics.categories >= 2 ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className={`font-medium ${analytics.categories >= 2 ? 'text-green-800' : 'text-gray-500'}`}>
                    Diverse Offerings
                  </p>
                  <p className="text-sm text-gray-500">
                    {analytics.categories >= 2 ? `${analytics.categories} different categories!` : 'Add stalls in 2+ categories'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stall Activity List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Stall Activity
          </CardTitle>
          <CardDescription>Detailed view of all your stalls</CardDescription>
        </CardHeader>
        <CardContent>
          {boxes.length === 0 ? (
            <div className="text-center py-8">
              <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No stalls created yet</p>
              <p className="text-sm text-gray-400 mt-1">Create your first honesty box to see analytics</p>
            </div>
          ) : (
            <div className="space-y-3">
              {boxes
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((box) => (
                  <div 
                    key={box.id} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`stall-row-${box.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {box.images && box.images.length > 0 ? (
                        <img 
                          src={box.images[0]} 
                          alt={box.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                          <Store className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-medium text-gray-800">{box.name}</h4>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate max-w-[200px]">{box.address}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm text-gray-500">Created</p>
                        <p className="text-sm font-medium text-gray-700">{getTimeSince(box.created_at)}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {box.is_active ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Eye className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">
                            <EyeOff className="w-3 h-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                        
                        {box.moderation_status === 'pending' && (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorAnalytics;
